import React, { useState, useEffect } from 'react';
import {
    Box,
    FormControl,
    FormControlLabel,
    Grid2,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
    Divider,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { generateCoupons, getCoupon, editCoupon } from '@openapi/coupons/coupons';
import { useGetShow } from '@openapi/shows/shows';
import useNotify from '../../hooks/useNotify';
import { formatDate } from '../../helpers/formatDate';
import CommonButton from '../../components/atom/CommonButton';
import PageWithTitle from '../../components/atom/PageWithTitle';
import BlockWithTitle from '../../components/atom/BlockWithTitle';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

function AddCouponsPage() {
    const { t } = useTranslation();
    const { showId } = useParams();
    const [searchParams] = useSearchParams();
    const couponId = searchParams.get('edit');
    const isEditMode = Boolean(couponId);

    const navigate = useNavigate();
    const { notifyError, notifySuccess } = useNotify();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editingCoupon, setEditingCoupon] = useState(null);
    const [loadingCoupon, setLoadingCoupon] = useState(isEditMode);

    const { data: show, isLoading: loadingShow } = useGetShow(Number(showId), {
        query: {
            onError: notifyError,
        },
    });

    // Load coupon data when in edit mode
    useEffect(() => {
        if (isEditMode && couponId) {
            setLoadingCoupon(true);
            getCoupon(Number(couponId))
                .then((coupon) => {
                    setEditingCoupon(coupon);
                })
                .catch((err) => {
                    notifyError(err?.message || t('errors.notFound'));
                    navigate(`/shows/${showId}/coupons`);
                })
                .finally(() => {
                    setLoadingCoupon(false);
                });
        }
    }, [isEditMode, couponId, notifyError, navigate, showId, t]);

    // Default expiration date 30 days from now
    const defaultExpiration = formatDate(
        new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        'date',
    );

    const getInitialValues = () => {
        if (isEditMode && editingCoupon) {
            return {
                discountType: editingCoupon.discountType || 'percentage',
                discountValue:
                    editingCoupon.discountType === 'fixed'
                        ? (editingCoupon.discountValue || 0) / 100
                        : editingCoupon.discountValue || 0,
                quantity: 1,
                expirationDate: editingCoupon.expirationDate
                    ? formatDate(editingCoupon.expirationDate, 'date')
                    : defaultExpiration,
                maxUses: editingCoupon.maxUses || '',
                minOrderAmount: editingCoupon.minOrderAmount
                    ? editingCoupon.minOrderAmount / 100
                    : '',
                useCustomCode: true,
                customCode: editingCoupon.code || '',
                isActive: editingCoupon.status === 'active',
            };
        }
        return {
            discountType: 'percentage',
            discountValue: 10,
            quantity: 1,
            expirationDate: defaultExpiration,
            maxUses: '',
            minOrderAmount: '',
            useCustomCode: false,
            customCode: '',
            isActive: true,
        };
    };

    const validateSchema = Yup.object().shape({
        discountType: Yup.string().oneOf(['percentage', 'fixed']).required(t('common.required')),
        discountValue: Yup.number()
            .positive(t('coupons.validation.positiveValue'))
            .required(t('common.required'))
            .when('discountType', {
                is: 'percentage',
                then: (schema) =>
                    schema
                        .max(100, t('coupons.validation.percentageMax'))
                        .min(1, t('coupons.validation.percentageMin')),
            }),
        quantity: Yup.number()
            .min(1, t('coupons.validation.quantityMin'))
            .max(1000, t('coupons.validation.quantityMax'))
            .required(t('common.required')),
        expirationDate: Yup.date().required(t('common.required')),
        maxUses: Yup.number().positive(t('coupons.validation.positiveValue')).nullable(),
        minOrderAmount: Yup.number().positive(t('coupons.validation.positiveValue')).nullable(),
        customCode: Yup.string().when('useCustomCode', {
            is: true,
            then: (schema) => schema.required(t('common.required')),
        }),
    });

    const onSubmit = async (values) => {
        setIsSubmitting(true);
        try {
            if (isEditMode) {
                // Edit existing coupon
                await editCoupon(Number(couponId), {
                    maxUses: values.maxUses ? Number(values.maxUses) : null,
                    expirationDate: values.expirationDate,
                    isActive: values.isActive,
                });
                notifySuccess(t('coupons.notifications.updated'));
            } else {
                // Create new coupons
                const payload = {
                    showId: Number(showId),
                    discountType: values.discountType,
                    discountValue:
                        values.discountType === 'fixed'
                            ? Math.round(values.discountValue * 100)
                            : values.discountValue,
                    quantity: values.quantity,
                    expirationDate: values.expirationDate,
                    maxUses: values.maxUses ? Number(values.maxUses) : null,
                    minOrderAmount: values.minOrderAmount
                        ? Math.round(values.minOrderAmount * 100)
                        : null,
                    codes:
                        values.useCustomCode && values.customCode ? [values.customCode] : undefined,
                };

                const result = await generateCoupons(payload);

                // Export to XLSX
                if (result?.coupons?.length) {
                    const exportData = result.coupons.map((coupon) => ({
                        Code: coupon.code,
                        'Discount Type': coupon.discountType,
                        'Discount Value':
                            coupon.discountType === 'percentage'
                                ? `${coupon.discountValue}%`
                                : `$${(coupon.discountValue / 100).toFixed(2)}`,
                        'Expiration Date': coupon.expirationDate,
                        'Max Uses': coupon.maxUses || 'Unlimited',
                    }));

                    const XLSX = await import('xlsx');
                    const ws = XLSX.utils.json_to_sheet(exportData);
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, 'Generated Coupons');
                    XLSX.writeFile(wb, `coupons-${show?.title || showId}-${Date.now()}.xlsx`);
                }

                notifySuccess(
                    t('coupons.notifications.generated', { count: result.coupons?.length }),
                );
            }
            navigate(`/shows/${showId}/coupons`);
        } catch (error) {
            notifyError(
                error?.message ||
                    t(
                        isEditMode
                            ? 'coupons.notifications.updateError'
                            : 'coupons.notifications.generateError',
                    ),
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loadingCoupon) {
        return (
            <PageWithTitle title={t('common.loading')}>
                <Typography>{t('common.loading')}...</Typography>
            </PageWithTitle>
        );
    }

    return (
        <Formik
            onSubmit={onSubmit}
            initialValues={getInitialValues()}
            validationSchema={validateSchema}
            enableReinitialize
        >
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
                <Form>
                    <PageWithTitle
                        title={
                            isEditMode
                                ? `${t('coupons.editModal.title')} - ${editingCoupon?.code || ''}`
                                : `${t('coupons.generate')} - ${loadingShow ? '' : show?.title || ''}`
                        }
                    >
                        <BlockWithTitle
                            paperStyle={{ width: '100%', textAlign: 'left' }}
                            title={
                                isEditMode ? t('coupons.editModal.title') : t('coupons.generate')
                            }
                        >
                            <Grid2 container spacing={3} sx={{ width: '100%' }}>
                                {/* Code Section */}
                                {isEditMode ? (
                                    <Grid2 size={{ xs: 12 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {t('coupons.columns.code')}
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
                                            {editingCoupon?.code}
                                        </Typography>
                                    </Grid2>
                                ) : (
                                    <>
                                        <Grid2 size={{ xs: 12 }}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        name="useCustomCode"
                                                        checked={values.useCustomCode}
                                                        onChange={handleChange}
                                                    />
                                                }
                                                label={t('coupons.useCustomCode')}
                                            />
                                        </Grid2>
                                        {values.useCustomCode ? (
                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                <TextField
                                                    fullWidth
                                                    name="customCode"
                                                    label={t('coupons.customCode')}
                                                    value={values.customCode}
                                                    onChange={(e) =>
                                                        setFieldValue(
                                                            'customCode',
                                                            e.target.value.toUpperCase(),
                                                        )
                                                    }
                                                    onBlur={handleBlur}
                                                    error={
                                                        touched.customCode &&
                                                        Boolean(errors.customCode)
                                                    }
                                                    helperText={
                                                        (touched.customCode && errors.customCode) ||
                                                        t('coupons.customCodeHelp')
                                                    }
                                                />
                                            </Grid2>
                                        ) : (
                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                <TextField
                                                    fullWidth
                                                    name="quantity"
                                                    label={t('coupons.quantity')}
                                                    type="number"
                                                    value={values.quantity}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={
                                                        touched.quantity && Boolean(errors.quantity)
                                                    }
                                                    helperText={
                                                        (touched.quantity && errors.quantity) ||
                                                        t('coupons.autoGenerateInfo', {
                                                            count: values.quantity,
                                                        })
                                                    }
                                                    inputProps={{ min: 1, max: 1000 }}
                                                />
                                            </Grid2>
                                        )}
                                    </>
                                )}

                                <Grid2 size={{ xs: 12 }}>
                                    <Divider sx={{ my: 1 }} />
                                </Grid2>

                                {/* Discount Section */}
                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                    <FormControl fullWidth disabled={isEditMode}>
                                        <InputLabel>{t('coupons.columns.discountType')}</InputLabel>
                                        <Select
                                            name="discountType"
                                            value={values.discountType}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            label={t('coupons.columns.discountType')}
                                        >
                                            <MenuItem value="percentage">
                                                {t('coupons.discountTypes.percentage')}
                                            </MenuItem>
                                            <MenuItem value="fixed">
                                                {t('coupons.discountTypes.fixed')}
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid2>
                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        fullWidth
                                        name="discountValue"
                                        label={t('coupons.columns.value')}
                                        type="number"
                                        value={values.discountValue}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        disabled={isEditMode}
                                        error={
                                            touched.discountValue && Boolean(errors.discountValue)
                                        }
                                        helperText={touched.discountValue && errors.discountValue}
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        {values.discountType === 'percentage'
                                                            ? '%'
                                                            : '$'}
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                    />
                                </Grid2>

                                {!isEditMode && (
                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            fullWidth
                                            name="minOrderAmount"
                                            label={t('coupons.minOrderAmount')}
                                            type="number"
                                            value={values.minOrderAmount}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                                touched.minOrderAmount &&
                                                Boolean(errors.minOrderAmount)
                                            }
                                            helperText={
                                                (touched.minOrderAmount && errors.minOrderAmount) ||
                                                t('coupons.minOrderAmountHelp')
                                            }
                                            slotProps={{
                                                input: {
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            $
                                                        </InputAdornment>
                                                    ),
                                                },
                                            }}
                                        />
                                    </Grid2>
                                )}

                                <Grid2 size={{ xs: 12 }}>
                                    <Divider sx={{ my: 1 }} />
                                </Grid2>

                                {/* Settings Section */}
                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        fullWidth
                                        name="expirationDate"
                                        label={t('coupons.columns.expirationDate')}
                                        type="date"
                                        value={values.expirationDate}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                            touched.expirationDate && Boolean(errors.expirationDate)
                                        }
                                        helperText={touched.expirationDate && errors.expirationDate}
                                        slotProps={{
                                            inputLabel: { shrink: true },
                                        }}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        fullWidth
                                        name="maxUses"
                                        label={t('coupons.maxUses')}
                                        type="number"
                                        value={values.maxUses}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.maxUses && Boolean(errors.maxUses)}
                                        helperText={
                                            (touched.maxUses && errors.maxUses) ||
                                            t('coupons.maxUsesHelp')
                                        }
                                    />
                                </Grid2>

                                {isEditMode && (
                                    <Grid2 size={{ xs: 12 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    name="isActive"
                                                    checked={values.isActive}
                                                    onChange={handleChange}
                                                />
                                            }
                                            label={t('coupons.fields.isActive')}
                                        />
                                        {editingCoupon?.currentUses > 0 && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ ml: 2 }}
                                            >
                                                {t('coupons.columns.uses')}:{' '}
                                                {editingCoupon.currentUses} /{' '}
                                                {editingCoupon.maxUses || t('coupons.unlimited')}
                                            </Typography>
                                        )}
                                    </Grid2>
                                )}
                            </Grid2>
                        </BlockWithTitle>

                        <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                            <CommonButton
                                type="submit"
                                disabled={isSubmitting}
                                label={isEditMode ? t('common.save') : t('coupons.generate')}
                                buttonType="primary"
                            />
                            <CommonButton
                                onClick={() => navigate(`/shows/${showId}/coupons`)}
                                label={t('common.back')}
                                buttonType="secondary"
                            />
                        </Box>
                    </PageWithTitle>
                </Form>
            )}
        </Formik>
    );
}

export default AddCouponsPage;
